"use client";

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import Button from "@/components/ui/Button";
import { ALL_LOCATIONS } from "@/hooks/useCatalogFilters";
import axiosInstance, { endpoints } from "@/lib/axios";
import { cn, formatFrenchDateLong } from "@/lib/utils";
import type { ISessionSlot } from "@/types/formation";
import { LEAD_SOURCES, type ILeadPayload } from "@/types/lead";
import useModalDismiss from "@/hooks/useModalDismiss";
import {
  INPUT_CLASS,
  INPUT_ERROR_CLASS,
  LABEL_CLASS,
  TEXTAREA_CLASS,
} from "./formStyles";

interface EnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Formation the request is for — the modal title, and `leads.formation_title`. */
  formationTitle: string;
  /** Formation category → `leads.formation_theme` (the admin "Thème" filter). */
  formationTheme?: string;
  /** Session being enrolled in. Its dates/location head the modal; null for a
   *  general "inscription à la formation" request. */
  session?: ISessionSlot | null;
}

// ---------------------------------------------------------------------------
// Form model
// ---------------------------------------------------------------------------

interface EnrollmentForm {
  prenom: string;
  nom: string;
  societe: string;
  email: string;
  phone: string;
  ville: string;
  /** Kept as a string because that's what a controlled number input yields. */
  participants: string;
  commentaire: string;
}

type FieldName = keyof EnrollmentForm;

const EMPTY_FORM: EnrollmentForm = {
  prenom: "",
  nom: "",
  societe: "",
  email: "",
  phone: "",
  ville: "",
  participants: "1",
  commentaire: "",
};

/** Tab order of the required fields — drives "focus the first invalid field". */
const REQUIRED_ORDER: FieldName[] = [
  "prenom",
  "nom",
  "societe",
  "email",
  "phone",
  "ville",
  "participants",
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "06 12 34 56 78" → "+33612345678". Leaves already-international numbers alone. */
function normalizePhone(value: string): string {
  const compact = value.replace(/[\s.\-()]/g, "");
  if (compact.startsWith("+")) return compact;
  if (compact.startsWith("0")) return `+33${compact.slice(1)}`;
  return compact;
}

// ---------------------------------------------------------------------------
// Field — label + control + error, so the grid below stays readable
// ---------------------------------------------------------------------------

interface FieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  className?: string;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({
  id,
  label,
  required,
  error,
  className,
  children,
}) => (
  <div className={cn("flex flex-col gap-1.5", className)}>
    <label htmlFor={id} className={LABEL_CLASS}>
      {label}
      {required && (
        <span className="text-[#FC7F16]" aria-hidden="true">
          {" "}
          *
        </span>
      )}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} role="alert" className="text-[11px] text-red-600">
        {error}
      </p>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Chevron — same arrow the session filters use
// ---------------------------------------------------------------------------

const SelectChevron: React.FC = () => (
  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5">
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
      <path
        d="M1 1L5 5L9 1"
        stroke="#1B365E"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

// ---------------------------------------------------------------------------
// EnrollmentModal
// ---------------------------------------------------------------------------

/**
 * "Demander une inscription" modal for a training session.
 *
 * Reuses the shell of the other fiche modals ({@link ContactModal},
 * DownloadProgramModal, SendAvisModal): same overlay, close button and design
 * tokens — and the same leads endpoint. Adds what a long form needs: dismissal
 * on Escape / backdrop click, a scroll-locked body, and per-field validation
 * messages instead of a silently disabled CTA.
 *
 * The parent owns the open/close state.
 */
const EnrollmentModal: React.FC<EnrollmentModalProps> = ({
  isOpen,
  onClose,
  formationTitle,
  formationTheme,
  session = null,
}) => {
  const uid = useId();
  const fieldId = (name: FieldName) => `${uid}-${name}`;

  const firstFieldRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<EnrollmentForm>(EMPTY_FORM);
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(() => {
    const next: Partial<Record<FieldName, string>> = {};

    if (!form.prenom.trim()) next.prenom = "Veuillez indiquer votre prénom.";
    if (!form.nom.trim()) next.nom = "Veuillez indiquer votre nom.";
    if (!form.societe.trim()) next.societe = "Veuillez indiquer votre société.";
    if (!EMAIL_PATTERN.test(form.email.trim())) {
      next.email = "Veuillez saisir une adresse de messagerie valide.";
    }
    if (form.phone.replace(/\D/g, "").length < 9) {
      next.phone = "Veuillez saisir un numéro de téléphone valide.";
    }
    if (!form.ville) next.ville = "Veuillez choisir une ville.";
    if (!(Number(form.participants) >= 1)) {
      next.participants = "Au moins un participant doit être inscrit.";
    }

    return next;
  }, [form]);

  const errorFor = (name: FieldName) => (attempted ? errors[name] : undefined);

  // Escape to dismiss + lock the page behind the modal.
  useModalDismiss(isOpen, onClose);

  // Send focus into the dialog when it opens.
  useEffect(() => {
    if (isOpen) firstFieldRef.current?.focus();
  }, [isOpen]);

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  /**
   * Only what the `leads` table has no column for. Société / Ville / the
   * formation itself are sent as real fields (see the payload below), so
   * repeating them here would just duplicate what the admin already displays.
   */
  const buildMessage = () =>
    [
      `Demande d'inscription — ${formationTitle}`,
      session &&
        `Session : ${formatFrenchDateLong(session.start_date)} → ${formatFrenchDateLong(
          session.end_date,
        )}${session.location ? ` (${session.location})` : ""}`,
      `Participants à inscrire : ${Number(form.participants)}`,
      form.commentaire.trim() && `Commentaire : ${form.commentaire.trim()}`,
    ]
      .filter(Boolean)
      .join("\n");

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    setAttempted(true);

    const firstInvalid = REQUIRED_ORDER.find((name) => errors[name]);
    if (firstInvalid) {
      document.getElementById(fieldId(firstInvalid))?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const payload: ILeadPayload = {
        first_name: form.prenom.trim(),
        last_name: form.nom.trim(),
        email: form.email.trim(),
        phone: normalizePhone(form.phone),
        company: form.societe.trim(),
        city: form.ville,
        message: buildMessage(),
        formation_title: formationTitle,
        // Capped: the API validates formation_theme at 150 characters.
        formation_theme: formationTheme?.slice(0, 150),
        origin: "formation",
        source: LEAD_SOURCES.session,
      };

      await axiosInstance.post(endpoints.leads.create, payload);

      toast.success(
        "Votre demande d'inscription a bien été envoyée. Nous vous recontactons rapidement.",
      );
      setForm(EMPTY_FORM);
      setAttempted(false);
      onClose();
    } catch {
      toast.error(
        "Une erreur est survenue lors de l'envoi. Veuillez réessayer.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const titleId = `${uid}-title`;

  return (
    <div
      className="fixed inset-0 z-[100050] flex items-center justify-center p-4 bg-[#FFFFFF4D] backdrop-blur-xs"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-170 max-h-[90vh] flex flex-col bg-white rounded-lg border border-[#40BFDE]"
        style={{ boxShadow: "0px 1px 2px 1px #00000014" }}
      >
        {/* Close Button — outside the scroll area so it stays reachable on a
            long form. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-white text-[#1B365E] hover:text-[#40BFDE] transition-colors cursor-pointer duration-75"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M23.2897 4.0987C24.2268 3.16164 24.2268 1.63986 23.2897 0.702796C22.3526 -0.234265 20.8309 -0.234265 19.8938 0.702796L12 8.60409L4.0987 0.710292C3.16164 -0.226769 1.63986 -0.226769 0.702796 0.710292C-0.234265 1.64735 -0.234265 3.16914 0.702796 4.1062L8.60409 12L0.710292 19.9013C-0.226769 20.8384 -0.226769 22.3601 0.710292 23.2972C1.64735 24.2343 3.16914 24.2343 4.1062 23.2972L12 15.3959L19.9013 23.2897C20.8384 24.2268 22.3601 24.2268 23.2972 23.2897C24.2343 22.3526 24.2343 20.8309 23.2972 19.8938L15.3959 12L23.2897 4.0987Z"
              fill="currentColor"
            />
          </svg>
        </button>

        <div className="overflow-y-auto pt-12 pb-6 sm:pb-10 px-5 sm:px-10">
          {/* Header — formation + chosen session */}
          <div className="text-center">
            <h2
              id={titleId}
              className="font-red-hat font-light text-[24px] sm:text-[32px] leading-8 sm:leading-10 text-[#1B365E] tracking-[0.9px]"
            >
              {formationTitle}
            </h2>

            {session && (
              <div className="mt-4 flex flex-col items-center gap-1.5">
                <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-[#1D1D1D] text-[14px] sm:text-[16px] font-bold leading-5">
                  <span>{formatFrenchDateLong(session.start_date)}</span>
                  <span className="text-[#ABABAB] font-normal">–</span>
                  <span>{formatFrenchDateLong(session.end_date)}</span>
                </div>
                {session.location && (
                  <span className={LABEL_CLASS}>{session.location}</span>
                )}
              </div>
            )}
          </div>

          <hr className="my-6 border-[#EFEFEF]" />

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate>
            <h3 className="font-red-hat font-normal text-[18px] sm:text-[20px] leading-7 text-[#1B365E] mb-4">
              Demandeur&nbsp;:
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <Field
                id={fieldId("prenom")}
                label="Prénom"
                required
                error={errorFor("prenom")}
              >
                <input
                  ref={firstFieldRef}
                  id={fieldId("prenom")}
                  name="prenom"
                  type="text"
                  autoComplete="given-name"
                  required
                  value={form.prenom}
                  onChange={handleChange}
                  aria-invalid={Boolean(errorFor("prenom"))}
                  aria-describedby={
                    errorFor("prenom") ? `${fieldId("prenom")}-error` : undefined
                  }
                  className={cn(
                    INPUT_CLASS,
                    errorFor("prenom") && INPUT_ERROR_CLASS,
                  )}
                />
              </Field>

              <Field
                id={fieldId("nom")}
                label="Nom"
                required
                error={errorFor("nom")}
              >
                <input
                  id={fieldId("nom")}
                  name="nom"
                  type="text"
                  autoComplete="family-name"
                  required
                  value={form.nom}
                  onChange={handleChange}
                  aria-invalid={Boolean(errorFor("nom"))}
                  aria-describedby={
                    errorFor("nom") ? `${fieldId("nom")}-error` : undefined
                  }
                  className={cn(INPUT_CLASS, errorFor("nom") && INPUT_ERROR_CLASS)}
                />
              </Field>

              <Field
                id={fieldId("societe")}
                label="Entreprise"
                required
                error={errorFor("societe")}
                className="sm:col-span-2"
              >
                <input
                  id={fieldId("societe")}
                  name="societe"
                  type="text"
                  autoComplete="organization"
                  required
                  value={form.societe}
                  onChange={handleChange}
                  aria-invalid={Boolean(errorFor("societe"))}
                  aria-describedby={
                    errorFor("societe") ? `${fieldId("societe")}-error` : undefined
                  }
                  className={cn(
                    INPUT_CLASS,
                    errorFor("societe") && INPUT_ERROR_CLASS,
                  )}
                />
              </Field>

              <Field
                id={fieldId("email")}
                label="Adresse de messagerie"
                required
                error={errorFor("email")}
              >
                <input
                  id={fieldId("email")}
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  aria-invalid={Boolean(errorFor("email"))}
                  aria-describedby={
                    errorFor("email") ? `${fieldId("email")}-error` : undefined
                  }
                  className={cn(
                    INPUT_CLASS,
                    errorFor("email") && INPUT_ERROR_CLASS,
                  )}
                />
              </Field>

              <Field
                id={fieldId("phone")}
                label="Numéro de téléphone"
                required
                error={errorFor("phone")}
              >
                <input
                  id={fieldId("phone")}
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  placeholder="06 12 34 56 78"
                  value={form.phone}
                  onChange={handleChange}
                  aria-invalid={Boolean(errorFor("phone"))}
                  aria-describedby={
                    errorFor("phone") ? `${fieldId("phone")}-error` : undefined
                  }
                  className={cn(
                    INPUT_CLASS,
                    errorFor("phone") && INPUT_ERROR_CLASS,
                  )}
                />
              </Field>

              <Field
                id={fieldId("ville")}
                label="Ville"
                required
                error={errorFor("ville")}
              >
                <div className="relative">
                  <select
                    id={fieldId("ville")}
                    name="ville"
                    required
                    value={form.ville}
                    onChange={handleChange}
                    aria-invalid={Boolean(errorFor("ville"))}
                    aria-describedby={
                      errorFor("ville") ? `${fieldId("ville")}-error` : undefined
                    }
                    className={cn(
                      INPUT_CLASS,
                      "appearance-none pr-8 cursor-pointer",
                      form.ville === "" && "text-[#ABABAB]",
                      errorFor("ville") && INPUT_ERROR_CLASS,
                    )}
                  >
                    <option value="" disabled>
                      Choisissez une ville
                    </option>
                    {ALL_LOCATIONS.map((location) => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </select>
                  <SelectChevron />
                </div>
              </Field>

              <Field
                id={fieldId("participants")}
                label="Nombre de participants à inscrire"
                required
                error={errorFor("participants")}
              >
                <input
                  id={fieldId("participants")}
                  name="participants"
                  type="number"
                  min={1}
                  step={1}
                  required
                  value={form.participants}
                  onChange={handleChange}
                  aria-invalid={Boolean(errorFor("participants"))}
                  aria-describedby={
                    errorFor("participants")
                      ? `${fieldId("participants")}-error`
                      : undefined
                  }
                  className={cn(
                    INPUT_CLASS,
                    errorFor("participants") && INPUT_ERROR_CLASS,
                  )}
                />
              </Field>

              <Field
                id={fieldId("commentaire")}
                label="Ajouter un commentaire et/ou les noms des participants"
                className="sm:col-span-2"
              >
                <textarea
                  id={fieldId("commentaire")}
                  name="commentaire"
                  rows={4}
                  maxLength={1500}
                  value={form.commentaire}
                  onChange={handleChange}
                  className={TEXTAREA_CLASS}
                />
              </Field>
            </div>

            <p className="mt-4 text-[12px] text-[#1B365E] font-medium tracking-[0.25px]">
              <span className="text-[#FC7F16]">*</span> Champs obligatoires
            </p>

            <Button
              type="submit"
              variant="primary"
              size="unset"
              disabled={submitting}
              className="mt-5 w-full h-12 sm:h-14.5 rounded-lg text-[16px]"
            >
              {submitting ? "Envoi..." : "Demander une inscription"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EnrollmentModal;
